/**
 * Documents domain — non-hook utilities.
 *
 * Domain type re-exports, the DRF error transform (load-bearing: baseAPICall
 * only surfaces `error_message`, but this API returns `{ detail }` / field
 * errors), and imperative helpers that sit outside the generated client:
 * multipart upload/replace (progress) and the polymorphic bulk download
 * (blob for one document, async JSON job for many).
 */

import { AxiosProgressEvent } from 'axios'
import { clientInstance } from '../../utils/http-client'
import { listDocuments } from '../generated/clients/listDocuments'
import type { ListDocumentsQueryParams } from '../generated/types/ListDocuments'

// ── Domain entity types (re-exported from generated) ────────────────
// Generated names CONFIRMED after Task 6:
//   DocumentList (entity, id: number), DocumentTagSummary ({id: uuid string,
//   tag, color?} — the shape in Document.tags and searchDocumentTags results;
//   the full CRUD `DocumentTag` type is for the settings page, not these
//   consumers), PaginatedDocumentListList (list envelope), DocumentContainerQuota.
export type { DocumentList as Document } from '../generated/types/DocumentList'
export type { DocumentTagSummary as DocumentTag } from '../generated/types/DocumentTagSummary'
export type { PaginatedDocumentListList as DocumentListResponse } from '../generated/types/PaginatedDocumentListList'
export type { DocumentContainerQuota } from '../generated/types/DocumentContainerQuota'

import type { DocumentList as DocumentEntity } from '../generated/types/DocumentList'
// `export type { X as Y } from '...'` re-exports Y but doesn't bind it locally
// (standard ES module re-export semantics), so `DocumentTag` isn't usable
// below — a second type-only import gives this file its own local alias,
// same as `DocumentEntity` does for `Document`.
import type { DocumentTagSummary as DocumentTagEntity } from '../generated/types/DocumentTagSummary'
// Same reasoning as `DocumentEntity`/`DocumentTagEntity` above: the type-only
// re-export doesn't bind `DocumentListResponse` locally, so `getDocuments`
// below needs its own alias to use as a return type.
import type { PaginatedDocumentListList as DocumentListResponseEntity } from '../generated/types/PaginatedDocumentListList'

/** Single-source the id type. Confirmed: DocumentList.id is `number`. */
export type DocumentId = DocumentEntity['id']

export interface FileExtensionOption {
  label: string
  value: string
}

export interface UploadDocumentParams {
  file: File
  name?: string
  tags?: string[]
  product_id?: number
  variant_id?: number
  contact_company_id?: number
  brand_id?: number
  deal_id?: number
}

export interface UpdateDocumentParams {
  name?: string
  tags?: string[]
  product_id?: number | null
  variant_id?: number | null
  contact_company_id?: number | null
  brand_id?: number | null
}

export interface ReplaceDocumentParams {
  file: File
  name?: string
}

export interface BulkAssignDocumentBrandRequest {
  brand_id: number | null
  document_ids: DocumentId[]
}

export interface BulkAssignDocumentBrandResponse {
  updated_count: number
}

// ── Document list (imperative) ──────────────────────────────────────

/**
 * Fetch a paginated, filtered list of documents.
 *
 * Imperative wrapper around the generated `listDocuments` client so
 * consumers that need a plain `Promise` — e.g. `DocumentsTable`'s own
 * `useQuery` keyed on the DataTable's table key, which must stay off
 * `useListDocuments`'s generated query key for invalidation reasons —
 * don't import from `generated/` directly. The generated client's
 * underlying `fetch` (see `apiClient.ts`) already resolves to
 * `{ data }` and `listDocuments` returns `res.data`, so the response
 * here is already the unwrapped `DocumentListResponse` body; no further
 * unwrapping needed.
 *
 * @param params - Filter, sort, and pagination query params
 * @param signal - AbortSignal for cancellation
 */
export async function getDocuments(
  params?: ListDocumentsQueryParams,
  signal?: AbortSignal
): Promise<DocumentListResponseEntity> {
  return listDocuments(params, { signal })
}

// ── Error transform ─────────────────────────────────────────────────

/**
 * Reduce an axios/DRF error to a user-facing string.
 *
 * Handles, in order: `{ detail }`, field-keyed arrays of DRF `ErrorDetail`
 * (`{ string }`) objects, field-keyed plain-string arrays, single field
 * values, and finally `error.message`.
 */
export function extractDocumentError(error: unknown): string {
  const anyErr = error as {
    response?: { data?: unknown }
    message?: string
  }
  const data = anyErr?.response?.data

  if (data && typeof data === 'object') {
    const record = data as Record<string, unknown>

    if (typeof record.detail === 'string') {
      return record.detail
    }

    const firstError = Object.values(record)[0]

    if (Array.isArray(firstError)) {
      const item = firstError[0]
      if (
        item &&
        typeof item === 'object' &&
        'string' in item &&
        typeof (item as { string: unknown }).string === 'string'
      ) {
        return (item as { string: string }).string
      }
      if (item != null) return String(item)
    }

    if (
      firstError &&
      typeof firstError === 'object' &&
      'string' in firstError &&
      typeof (firstError as { string: unknown }).string === 'string'
    ) {
      return (firstError as { string: string }).string
    }

    if (typeof firstError === 'string') return firstError
    if (firstError != null) return String(firstError)
  }

  return anyErr?.message || 'Request failed'
}

// ── Header / filename helpers ───────────────────────────────────────

function getHeader(
  headers: Record<string, unknown>,
  name: string
): string | undefined {
  const lower = name.toLowerCase()
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lower) {
      const value = headers[key]
      return typeof value === 'string' ? value : undefined
    }
  }
  return undefined
}

function extractFilenameFromHeader(
  contentDisposition: string | undefined
): string | null {
  if (!contentDisposition) return null
  const match = contentDisposition.match(/filename="?([^";\n]+)"?/)
  return match ? match[1] : null
}

// ── Multipart upload / replace (imperative, with progress) ──────────

/**
 * Upload a document with optional metadata.
 *
 * Callers should catch and pass the raw axios error to
 * `extractDocumentError` to get a user-facing message — this function
 * does not pre-transform errors itself.
 *
 * @param params - File plus optional name/tags/entity associations
 * @param onProgress - Upload progress callback (0-100)
 */
export async function uploadDocument(
  params: UploadDocumentParams,
  onProgress?: (progress: number) => void
): Promise<DocumentEntity> {
  const formData = new FormData()
  formData.append('file', params.file)
  if (params.name) formData.append('name', params.name)
  if (params.tags && params.tags.length > 0)
    formData.append('tags', JSON.stringify(params.tags))
  if (params.product_id)
    formData.append('product_id', params.product_id.toString())
  if (params.variant_id)
    formData.append('variant_id', params.variant_id.toString())
  if (params.contact_company_id)
    formData.append('contact_company_id', params.contact_company_id.toString())
  if (params.brand_id != null)
    formData.append('brand_id', params.brand_id.toString())
  if (params.deal_id != null)
    formData.append('deal_id', params.deal_id.toString())

  const response = await clientInstance.post<DocumentEntity>(
    '/documents/',
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (onProgress && e.total)
          onProgress(Math.round((e.loaded * 100) / e.total))
      },
    }
  )
  return response.data
}

/**
 * Replace a document with a new file version, keeping its id/tags/metadata.
 *
 * Same error-handling contract as `uploadDocument`: throws the raw axios
 * error, transform with `extractDocumentError` at the call site.
 *
 * @param documentId - Id of the document to replace
 * @param params - Replacement file plus optional new name
 * @param onProgress - Upload progress callback (0-100)
 */
export async function replaceDocument(
  documentId: DocumentId,
  params: ReplaceDocumentParams,
  onProgress?: (progress: number) => void
): Promise<DocumentEntity> {
  const formData = new FormData()
  formData.append('file', params.file)
  if (params.name) formData.append('name', params.name)

  const response = await clientInstance.post<DocumentEntity>(
    `/documents/${documentId}/replace/`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e: AxiosProgressEvent) => {
        if (onProgress && e.total)
          onProgress(Math.round((e.loaded * 100) / e.total))
      },
    }
  )
  return response.data
}

// ── Polymorphic bulk download + status poll ─────────────────────────

export interface BulkDocumentDownloadResult {
  blob: Blob
  contentType: string
  filename: string
}

export interface AsyncBulkDocumentDownloadResponse {
  request_id: number
  status: 'processing' | 'completed' | 'failed' | 'expired'
  progress_percentage: number
  document_count: number
}

export interface BulkDocumentDownloadStatusResponse extends AsyncBulkDocumentDownloadResponse {
  download_url?: string
  detail?: string
  expires_at?: string
}

/**
 * Bulk-download documents — response shape depends on the request size.
 *
 * Single document: the server streams the file back immediately, so this
 * resolves with the `Blob` plus content-type/filename read from headers.
 * Multiple documents: the server kicks off an async zip job and returns a
 * `request_id` to poll via `getBulkDocumentDownloadStatus`.
 *
 * @param documentIds - Ids of the documents to download
 * @param signal - AbortSignal to cancel the request
 * @param onProgress - Download progress callback (0-100)
 */
export async function bulkDownloadDocuments(
  documentIds: DocumentId[],
  signal?: AbortSignal,
  onProgress?: (progress: number) => void
): Promise<BulkDocumentDownloadResult | AsyncBulkDocumentDownloadResponse> {
  const request = { include_ids: documentIds }
  const onDownloadProgress = (e: AxiosProgressEvent) => {
    if (onProgress && e.total)
      onProgress(Math.round((e.loaded / e.total) * 100))
  }

  if (documentIds.length === 1) {
    const response = await clientInstance.post(
      '/documents/bulk-download/',
      request,
      {
        responseType: 'blob',
        signal,
        onDownloadProgress,
      }
    )
    const contentType =
      getHeader(response.headers, 'content-type') ?? 'application/octet-stream'
    const contentDisposition = getHeader(
      response.headers,
      'content-disposition'
    )
    const fallback = `document_${new Date().toISOString().split('T')[0]}`
    return {
      blob: response.data,
      contentType,
      filename: extractFilenameFromHeader(contentDisposition) || fallback,
    }
  }

  const response = await clientInstance.post(
    '/documents/bulk-download/',
    request,
    { signal, onDownloadProgress }
  )
  return response.data as AsyncBulkDocumentDownloadResponse
}

/**
 * Poll the status of an async bulk-download job started by
 * `bulkDownloadDocuments`.
 *
 * @param requestId - The `request_id` returned by `bulkDownloadDocuments`
 */
export async function getBulkDocumentDownloadStatus(
  requestId: number
): Promise<BulkDocumentDownloadStatusResponse> {
  const response = await clientInstance.get(
    `/documents/bulk-download/${requestId}/status/`
  )
  return response.data
}

// ── Tag autocomplete (simple mode array) ────────────────────────────

/**
 * Search document tags for autocomplete.
 *
 * Passes `simple=true` so the backend returns a flat array instead of the
 * paginated envelope (see `DocumentTagPagination` on the server) — the
 * autocomplete widget just needs the matching tags, not pagination.
 *
 * @param search - Search query
 */
export async function searchDocumentTags(
  search: string
): Promise<DocumentTagEntity[]> {
  const response = await clientInstance.get<DocumentTagEntity[]>(
    '/documents/tags/',
    {
      params: { simple: 'true', search },
    }
  )
  return response.data
}
